import { Link } from "react-router-dom";
import { ArrowRight, Heart, Quote, Sparkles, Store, UsersRound } from "lucide-react";
import { IMAGES } from "@/data/mockData";

const VALUES = [
  {
    n: "01",
    icon: Sparkles,
    title: "Quality worth making with",
    body:
      "Beautiful yarns, dependable needles, and thoughtful accessories selected for makers who care about how a project feels in the hand.",
  },
  {
    n: "02",
    icon: UsersRound,
    title: "A welcoming place",
    body:
      "Cactus Rose was imagined as a shop where makers can find inspiration, friendly service, and a community that understands handmade work.",
  },
  {
    n: "03",
    icon: Heart,
    title: "Creativity, one stitch at a time",
    body:
      "Every project begins with excitement. The shop was built with that same patience, passion, and belief in making things by hand.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-cream">
      {/* Hero */}
      <section className="py-20 md:py-32">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-4">Our story</p>
              <h1 className="display-serif text-[52px] sm:text-[68px] md:text-[88px] lg:text-[104px] text-espresso">
                A yarn shop born
                <br />
                {" "}from <span className="italic text-clay">two dreams.</span>
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-ink text-lg leading-relaxed max-w-md">
                Cactus Rose Needle Arts began with Derek and Brent, a shared love
                of creativity, and a belief that the fiber arts community deserves
                a welcoming place to gather, learn, and begin the next beautiful
                project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial photography */}
      <section className="pb-20 md:pb-28" data-testid="about-editorial-photo">
        <div className="container-wide">
          <div className="aspect-[21/9] overflow-hidden bg-oat relative">
            <img
              src={IMAGES.store}
              alt="Warm editorial yarn shop shelves and fiber arts materials"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="bg-bone border-y border-rule py-20 md:py-28" data-testid="about-story">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">Our story</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] max-w-md">
              Two lifelong dreams, brought together for Las Vegas makers.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ink text-base md:text-lg leading-relaxed">
            <p>
              Cactus Rose Needle Arts was born from two lifelong dreams and a
              shared love of creativity, handmade crafts, and the fiber arts
              community.
            </p>
            <p>
              For Derek, owning a yarn shop had been a dream for as long as he
              could remember. A passionate knitter and crocheter from a very young
              age, he loved the joy that comes from creating something beautiful
              with yarn.
            </p>
            <p>
              Through countless projects, years of learning, and sharing his
              crafting journey with others, Derek imagined a place where fellow
              makers could find inspiration, quality products, and a welcoming
              community.
            </p>
            <p>
              For Brent, the dream went back just as far. Since childhood, he had
              imagined owning a store of his own: building something meaningful,
              helping customers, and creating a place people would enjoy visiting.
            </p>
            <p className="border-l border-clay pl-6 text-espresso">
              As the Las Vegas area lost several local yarn and crafting shops,
              the time felt right to bring those dreams together as Cactus Rose
              Needle Arts.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-cream" data-testid="about-values">
        <div className="container-editorial">
          <p className="eyebrow mb-4">Three commitments</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-16 max-w-2xl">
            What every maker should
            <br />
            {" "}<span className="italic text-forest">feel here.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {VALUES.map((v) => (
              <div key={v.n} className="border-t border-rule pt-6">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <p className="text-[10px] uppercase tracking-eyebrow text-clay">
                    {v.n}
                  </p>
                  <v.icon className="w-5 h-5 text-clay" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-3">
                  {v.title}
                </h3>
                <p className="text-ink leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Bella */}
      <section className="bg-bone border-y border-rule py-20 md:py-28" data-testid="about-bella">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-square bg-cream border border-rule flex items-center justify-center p-10">
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-full border border-rule bg-bone font-serif text-6xl italic text-clay">
                  B
                </div>
                <p className="eyebrow text-clay">Bella</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow mb-4">Meet Bella</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] max-w-2xl">
              A beloved companion, still stitched into the story.
            </h2>
            <div className="mt-6 space-y-5 text-ink text-base md:text-lg leading-relaxed">
              <p>
                Many Cactus Rose social media posts feature a cheerful West
                Highland White Terrier inspired by Bella, Derek's beloved Westie.
              </p>
              <p>
                Bella appeared in many of Derek's knitting and crochet videos and
                was a constant companion during countless crafting sessions.
                Although she passed away in 2022, her spirit remains part of the
                brand, and the cartoon Westie is a loving tribute to her.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Cactus Rose */}
      <section className="py-24 md:py-32 bg-espresso text-cream" data-testid="about-name">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4 text-cream/70">Why "Cactus Rose"</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-cream">
              Beauty grown through creativity, patience, and passion.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-cream/80 text-base md:text-lg leading-relaxed">
            <p>
              The name Cactus Rose Needle Arts reflects both a love of the
              American Southwest and an appreciation for the beauty that grows
              through creativity, patience, and passion.
            </p>
            <p>
              Like every handmade project, the business has been built one stitch
              at a time.
            </p>
            <p>
              You will often find both founders in the shop, and you may also meet
              Derek's mother, Claudia, who relocated from Texas to help launch the
              store.
            </p>
          </div>
        </div>
      </section>

      {/* Founders note */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <Quote className="w-6 h-6 text-clay mx-auto mb-6" strokeWidth={1.5} />
          <blockquote className="font-serif text-3xl md:text-5xl leading-[1.1] italic text-espresso">
            Thank you for visiting Cactus Rose Needle Arts and for being part of
            our journey.
          </blockquote>
          <p className="mt-8 text-[11px] uppercase tracking-eyebrow text-ink">
            Happy stitching, Derek & Brent
          </p>
        </div>
      </section>

      {/* Visit the Shop CTA */}
      <section className="bg-bone border-y border-rule py-20 md:py-24" data-testid="about-cta">
        <div className="container-editorial text-center">
          <Store className="w-6 h-6 text-clay mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            Come begin the next project.
          </h2>
          <p className="mt-6 mx-auto max-w-2xl text-ink text-base md:text-lg leading-relaxed">
            As Cactus Rose continues to grow, the mission remains simple: provide
            quality products, inspire creativity, and bring together people who
            love making things by hand.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/visit" data-testid="about-visit-cta" className="btn-primary">
              Visit the shop
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link to="/shop" className="btn-secondary">
              Browse yarn
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
